import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CertSection = styled.section`
  padding: ${p => p.theme.spacing.xxl} ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 700px;
  height: 400px;
  background: ${p => p.theme.gradients.glow};
  filter: blur(140px);
  opacity: 0.3;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const Label = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${p => p.theme.gradients.subtle};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${p => p.theme.typography.weights.medium};
  color: ${p => p.theme.colors.accentPurple};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const Title = styled(motion.h2)`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: ${p => p.theme.typography.weights.extrabold};
  margin-bottom: ${p => p.theme.spacing.sm};
  background: ${p => p.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(0.9375rem, 1.5vw, 1.0625rem);
  color: ${p => p.theme.colors.subtextGray};
  max-width: 540px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.lg};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${p => p.theme.spacing.md};
  }
`;

const CertCard = styled(motion.div)`
  background: ${p => p.theme.colors.surfaceCard};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 24px;
  padding: ${p => p.theme.spacing.xl};
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-6px);
    border-color: ${p => p.theme.colors.accentPurple};
    box-shadow: ${p => p.theme.shadows.cardHover};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${p => p.theme.gradients.primary};
  }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.lg};
  }
`;

const CertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const CertSeal = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${p => p.theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: ${p => p.theme.shadows.glow};
  flex-shrink: 0;
`;

const CertHeadings = styled.div`
  flex: 1;
  min-width: 0;
`;

const CertEyebrow = styled.div`
  font-size: 0.6875rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.subtextGray};
  margin-bottom: 4px;
  font-weight: ${p => p.theme.typography.weights.semibold};
`;

const CertTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: ${p => p.theme.typography.weights.bold};
  color: ${p => p.theme.colors.textLight};
  margin: 0;
  letter-spacing: -0.01em;
`;

const CertDescription = styled.p`
  font-size: 0.9375rem;
  color: ${p => p.theme.colors.subtextGray};
  line-height: 1.6;
  margin: 0 0 ${p => p.theme.spacing.md};
`;

const CertList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${p => p.theme.spacing.md};
  display: grid;
  gap: 6px;
`;

const CertListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${p => p.theme.colors.textDark};
`;

const Bullet = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${p => p.theme.gradients.subtle};
  color: ${p => p.theme.colors.accentPurple};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  flex-shrink: 0;
`;

const VerifyChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: #16A34A;
  letter-spacing: 0.02em;
`;

const VerifyDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22C55E;
  box-shadow: 0 0 6px #22C55E;
`;

/* ── Seal icons ── */
const AwardSeal = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="6" />
    <path d="M8.5 13L7 22l5-3 5 3-1.5-9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HeartSeal = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const certifications = [
  {
    eyebrow: 'Lean Foundation',
    title: 'Lean White Belt Credential',
    description: 'Eight interactive modules covering value, waste, flow, and problem solving — completed by passing the final assessment.',
    seal: AwardSeal,
    items: [
      '8 modules with knowledge checks',
      'Capstone Waste Hunt game',
      'Final assessment with passing score',
      'Public verification at /credentials/white-belt/...',
    ],
  },
  {
    eyebrow: 'Behavioral Mastery',
    title: 'Respect for People Credential',
    description: 'A multi-day program of scenarios, reflection journaling, and daily practice that culminates in a shareable certificate.',
    seal: HeartSeal,
    items: [
      'Interactive scenario challenges',
      'Daily practice and reflection log',
      'Manager insights dashboard',
      'Public verification at /credentials/respect-for-people/...',
    ],
  },
];

const Certifications = () => {
  return (
    <CertSection id="credentials">
      <BackgroundGlow />
      <Container>
        <Header>
          <Label
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Credentials
          </Label>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Earn shareable certifications
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Each credential gets its own public verification URL — perfect for resumes, LinkedIn, and internal records.
          </Subtitle>
        </Header>

        <Grid>
          {certifications.map((cert, idx) => {
            const Seal = cert.seal;
            return (
              <CertCard
                key={cert.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + idx * 0.08 }}
              >
                <CertHeader>
                  <CertSeal>
                    <Seal />
                  </CertSeal>
                  <CertHeadings>
                    <CertEyebrow>{cert.eyebrow}</CertEyebrow>
                    <CertTitle>{cert.title}</CertTitle>
                  </CertHeadings>
                </CertHeader>

                <CertDescription>{cert.description}</CertDescription>

                <CertList>
                  {cert.items.map((item) => (
                    <CertListItem key={item}>
                      <Bullet>✓</Bullet>
                      {item}
                    </CertListItem>
                  ))}
                </CertList>

                <VerifyChip>
                  <VerifyDot />
                  Public verification URL
                </VerifyChip>
              </CertCard>
            );
          })}
        </Grid>
      </Container>
    </CertSection>
  );
};

export default Certifications;
